import React, { useState } from 'react';
import { Clock, MapPin, ChevronDown, ChevronUp, Users } from 'lucide-react';

interface Speaker {
  name: string;
  title: string;
  company: string;
  avatar?: string;
}

interface Session {
  id: string;
  time: string;
  title: string;
  type: 'opening' | 'panel' | 'presentation' | 'break' | 'networking' | 'intro';
  description?: string;
  speakers?: Speaker[];
  location?: string;
  category?: string;
}

const ProgrammePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);

  const toggleSession = (sessionId: string) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandedSessions(new Set());
    } else {
      const allSessionIds = sessions.filter(s => s.description || (s.speakers && s.speakers.length > 0)).map(s => s.id);
      setExpandedSessions(new Set(allSessionIds));
    }
    setExpandAll(!expandAll);
  };

  const categories = [
    { id: 'all', name: 'All Topics' },
    { id: 'main-stage', name: 'I Glavna bina (Main Stage)' },
    { id: 'side-stage', name: 'II Sporedna bina (Side Stage)' },
    { id: 'b2b', name: 'III B2B i networking' }
  ];

  const locationMain = 'Glavna bina · Beogradski sajam, Hala 5';
  const locationSide = 'Sporedna bina · Beogradski sajam, Hala 5';
  const locationVenue = 'Beogradski sajam, Hala 5';

  const sessions: Session[] = [
    {
      id: '1',
      time: '08:00 – 09:00',
      title: 'Registracija učesnika, welcome kafa i doručak',
      type: 'break',
      location: locationVenue,
      category: 'all'
    },
    {
      id: '2',
      time: '09:00',
      title: 'Svečano otvaranje',
      type: 'opening',
      description: 'Svečano otvaranje konferencije LOGISTICS MARKET EXPO & CONFERENCE 2026.',
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '3',
      time: 'U nastavku',
      title: 'Pojedinačna prezentacija — Tailwind',
      type: 'presentation',
      speakers: [
        {
          name: 'Alexander Prahl',
          title: 'Global Partnerships & Business Development',
          company: 'Tailwind',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Alexander-Prahl_Tailwind.jpg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '4',
      time: 'U nastavku · PANEL 1',
      title: 'Intermodalni transport — izazovi i rešenja za konkurentniji region',
      type: 'panel',
      description: 'Moderator: Nemanja Živanović, član Uprave, Dry Port Terminals',
      speakers: [
        {
          name: 'Ivana Plavšić',
          title: 'CEO',
          company: 'Metrans Panonija',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Ivana-Plavsic-_Metrans.png'
        },
        {
          name: 'Iñigo Nuñez Mendibe',
          title: 'CEO',
          company: 'Adriatic Gate Container Terminal',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Inigo-Nunez-Mendibe_AGCT-.png'
        },
        {
          name: 'Sedat Kara',
          title: 'CEO',
          company: 'Port of Adria',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Sedat-Kara_Port-of-Adria.jpeg'
        },
        {
          name: 'Ivić Vodopija',
          title: 'East Europe Managing Director',
          company: 'Maersk Line',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Ivic-Vodopija-_Maersk.jpg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '5',
      time: 'U nastavku · PANEL 2',
      title: 'Break-bulk teret — mogućnosti, kapaciteti i razvoj regionalnih transportnih pravaca',
      type: 'panel',
      description: 'Moderator: Nemanja Živanović, član Uprave, Dry Port Terminals',
      speakers: [
        {
          name: 'Benjamin Herman',
          title: 'Head of Business Development',
          company: 'Tanta',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Benjamin-novo.jpeg'
        },
        {
          name: 'Uglješa Ugo Stojaković',
          title: 'Board member',
          company: 'Centralog',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/ugljesa-novo1.png'
        },
        {
          name: 'Nenad Bojanić',
          title: 'CEO',
          company: 'Fining',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Nenad-Bojanic-Fining-scaled-e1789040159301.jpg'
        },
        {
          name: 'Aleš Kocijan',
          title: 'General Cargo Terminal Director',
          company: 'Luka Koper',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Ales-Kocjan.jpg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '6',
      time: '60 min',
      title: 'Pauza za kafu i networking',
      type: 'break',
      location: locationVenue,
      category: 'main-stage'
    },
    {
      id: '7',
      time: 'U nastavku',
      title: 'Pojedinačna prezentacija — iQ Solutions & Quicktron Robotics',
      type: 'presentation',
      speakers: [
        {
          name: 'Prof. dr Sanja Bojić',
          title: 'CEO',
          company: 'iQ Solutions',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Sanja-Bojic_iQ-Solutions.jpeg'
        },
        {
          name: 'Nikola Todorović',
          title: 'Sales Director',
          company: 'Quicktron Robotics',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Nikola-Todorovic_Quicktron.jpeg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '8',
      time: 'U nastavku · PANEL 3',
      title: 'Pametno planiranje, izgradnja i opremanje distributivnih centara',
      type: 'panel',
      description: 'Moderator: Prof. dr Sanja Bojić, CEO, iQ Solutions',
      speakers: [
        {
          name: 'Peter Ekart',
          title: 'Director Sales & Business Development',
          company: 'LNConsult',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Peter-Ekart_LnConsult.jpeg'
        },
        {
          name: 'Momčilo Jakšić',
          title: 'CEO',
          company: 'Letač',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/placholder-img.webp'
        },
        {
          name: 'Ilija Zolotić',
          title: 'CEO',
          company: 'ZIK-SLO',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Ilija-zolotic.jpg'
        },
        {
          name: 'Andrej Sirk',
          title: 'General Manager',
          company: 'T.L. Sirk d.o.o',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Andrej-Sirk_TLS.jpeg'
        },
        {
          name: 'Maja Bogunović',
          title: 'Director',
          company: 'LogiStar',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Maja-Bogunovic-Logistar.jpg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '9',
      time: 'U nastavku · PANEL 4',
      title: 'Automatizacija i robotizacija najvišeg nivoa u distributivnim centrima',
      type: 'panel',
      description: 'Moderator: Prof. dr Sanja Bojić, CEO, iQ Solutions',
      speakers: [
        {
          name: 'Bojan Dobrota',
          title: 'CEO',
          company: 'Logit',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/BojanDobrota.width-480.jpg'
        },
        {
          name: 'Ivan Pozgaj',
          title: 'Business Development Manager',
          company: 'Element Logic',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Ivan_Pozgaj_-Element-Logic.webp'
        },
        {
          name: 'Mladen Marković',
          title: 'Business Development',
          company: 'tpsX',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Mladen-Markovic-tpsx-e1789042312743.jpeg'
        },
        {
          name: 'Aleksandar Grujić',
          title: 'Director Warehouse & Automation Projects',
          company: 'Jungheinrich',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Aleksandar-Grujic-Jungheinrich.jpg'
        },
        {
          name: 'Benito de Jesus Vigo Nieves',
          title: 'Director',
          company: 'HOERMANN INTRALOGISTICS SOLUTIONS',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Benito-de-Jesus-Vigo-Nieves.jpg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '10',
      time: 'U nastavku · PANEL 5',
      title: 'Dostava poslednjeg kilometra (Last Mile) — nova rešenja za završnu i najzahtevniju fazu isporuke',
      type: 'panel',
      description: 'Moderator: biće najavljen',
      speakers: [
        {
          name: 'Engin Ganić',
          title: 'Sales Manager',
          company: 'Smartivo',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/engin-ganic.jpg'
        },
        {
          name: 'Julija Caran',
          title: 'Commercial Director',
          company: 'FedEx',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Julija-Caran_FedEx.jpeg'
        },
        {
          name: 'Slobodan Simović',
          title: 'COO',
          company: 'tpsX',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Slobodan-Simovic.png'
        },
        {
          name: 'Nikola Riznić',
          title: 'CEO',
          company: 'X-express Srbija',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/nikola-riznic.jpg'
        },
        {
          name: 'Dušan Osmajilić',
          title: 'Transportation Manager',
          company: 'CIAK Auto',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Dusan-Osmajilic.jpg'
        }
      ],
      location: locationMain,
      category: 'main-stage'
    },
    {
      id: '11',
      time: '10:30 · PANEL 1',
      title: 'Izazovi u digitalizaciji preduzeća – koji su znaci da je potrebna i kada treba početi',
      type: 'panel',
      description: 'Moderator: Prof. dr Sanja Bojić, CEO, iQ Solutions\n\nProgram od 09:30, paralelno sa glavnom binom.',
      speakers: [
        {
          name: 'Uroš Brezovnik',
          title: 'IT Software Consultant',
          company: 'LNConsult',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Uros-Brezovik-LNC.png'
        },
        {
          name: 'Sara Lovrenčić',
          title: 'CEO',
          company: 'SISTEIV',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/placholder-img.webp'
        },
        {
          name: 'Mišo Božić',
          title: 'CEO',
          company: 'Faro Logistics',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Miso-Bozic_Faro-Logistics.jpeg'
        },
        {
          name: 'Vladimir Jović',
          title: 'CEO',
          company: 'Dubrast Consulting',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/vladimir-jovic.jpg'
        }
      ],
      location: locationSide,
      category: 'side-stage'
    },
    {
      id: '12',
      time: '60 min',
      title: 'Pauza za kafu i networking',
      type: 'break',
      location: locationVenue,
      category: 'side-stage'
    },
    {
      id: '13',
      time: 'U nastavku · PANEL 2',
      title: 'Alternativni železnički koridori, rečni transport i suve luke — rešenja za stabilniji lanac snabdevanja',
      type: 'panel',
      description: 'Moderator: Sanja Đurišić, Predsednik Grupacije luka i pristaništa',
      speakers: [
        {
          name: 'Slobodan Lukić',
          title: 'Commercial Steering Senior Expert',
          company: 'LKW Walter',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Slobodan-Lukic-_LKW-Walter.jpeg'
        },
        {
          name: 'Miloš Bijelić',
          title: 'Commercial Officer',
          company: 'DP World',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Milos-Bijelic_DP-World.jpeg'
        },
        {
          name: 'Vladimir Zečević',
          title: 'Chartering Department Director',
          company: 'IVBC International Vessel & Barge Chartering',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Vladimir-IVBC-e1789124429455.jpg'
        },
        {
          name: 'Biljana Vukotić',
          title: '',
          company: 'Luka Dunav Pančevo',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/placholder-img.webp'
        },
        {
          name: 'Lotesa Manojlović',
          title: 'Managing Director',
          company: 'Dry Port Terminals',
          avatar: 'https://events.logisticsmarket.rs/wp-content/uploads/2026/09/Lotesa-Manojlovic.png'
        }
      ],
      location: locationSide,
      category: 'side-stage'
    },
    {
      id: '14',
      time: '13:30 – 15:30',
      title: 'B2B sastanci',
      type: 'networking',
      description: 'Organizovani B2B sastanci i poslovni networking za učesnike, partnere i izlagače.',
      location: locationVenue,
      category: 'b2b'
    },
    {
      id: '15',
      time: '15:30',
      title: 'Ručak i networking',
      type: 'break',
      location: locationVenue,
      category: 'b2b'
    }
  ];

  const filteredSessions = selectedCategory === 'all' 
    ? sessions 
    : sessions.filter(session => session.category === selectedCategory);

  const getSessionTypeColor = (type: Session['type']) => {
    switch (type) {
      case 'opening':
        return 'bg-yellow-50 border-l-4 border-yellow-500';
      case 'panel':
        return 'bg-white border-l-4 border-gray-300';
      case 'presentation':
        return 'bg-white border-l-4 border-gray-300';
      case 'break':
        return 'bg-gray-50 border-l-4 border-gray-400';
      case 'networking':
        return 'bg-white border-l-4 border-gray-300';
      default:
        return 'bg-white border-l-4 border-gray-300';
    }
  };

  const getCategoryLabel = (category?: string) => {
    switch (category) {
      case 'main-stage':
        return 'I Glavna bina (Main Stage)';
      case 'side-stage':
        return 'II Sporedna bina (Side Stage)';
      case 'b2b':
        return 'III B2B i networking';
      default:
        return '';
    }
  };

  const getCategorySubtitle = (category: string) => {
    switch (category) {
      case 'main-stage':
        return 'Program: 09:00 – 15:30';
      case 'side-stage':
        return 'Program: od 09:30 (paralelno sa glavnom binom)';
      case 'b2b':
        return 'Organizovani B2B sastanci i networking';
      default:
        return '';
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Program</h1>
        <p className="text-gray-600">LOGISTICS MARKET EXPO & CONFERENCE 2026</p>
        <p className="text-sm text-gray-500">6. oktobar 2026 · Beogradski sajam, Hala 5 · 500+ učesnika</p>
      </div>

      {/* Category Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedCategory === category.id
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
              style={selectedCategory === category.id ? { backgroundColor: '#C89116' } : {}}
            >
              {category.name}
            </button>
          ))}
        </div>
        
        <button
          onClick={toggleExpandAll}
          className="text-sm font-medium px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
          style={{ color: '#C89116', borderColor: '#C89116' }}
        >
          {expandAll ? 'Collapse All -' : 'Expand All +'}
        </button>
      </div>

      {/* Section Title */}
      {selectedCategory !== 'all' && (
        <div className="mb-6 p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5" style={{ color: '#C89116' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {getCategoryLabel(selectedCategory)}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {getCategorySubtitle(selectedCategory)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-4">
        {filteredSessions.map((session) => (
          <div
            key={session.id}
            className={`rounded-lg p-4 sm:p-6 transition-all duration-200 ${getSessionTypeColor(session.type)}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4 mb-3">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 flex-shrink-0" style={{ color: '#C89116' }} />
                    <span className="font-bold text-base sm:text-lg text-gray-800">
                      {session.time}
                    </span>
                  </div>
                  {session.location && (
                    <div className="flex items-start text-gray-600 min-w-0">
                      <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span className="text-sm break-words">{session.location}</span>
                    </div>
                  )}
                </div>
                
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 break-words">
                  {session.title}
                </h3>
                
                {session.speakers && session.speakers.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center space-x-2 text-gray-600 mb-2">
                      <Users className="w-4 h-4" />
                      <span className="text-sm font-medium">Govornici:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {session.speakers.map((speaker, index) => (
                        <div key={index} className="bg-white/70 px-3 py-2 rounded-lg flex items-center space-x-3 max-w-full">
                          {speaker.avatar && (
                            <img 
                              src={speaker.avatar} 
                              alt={speaker.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          )}
                          <div>
                          <p className="font-semibold text-gray-800 text-sm">{speaker.name}</p>
                          {speaker.title && <p className="text-xs text-gray-600">{speaker.title}</p>}
                          <p className="text-xs text-gray-500">{speaker.company}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Expandable Description */}
                {session.description && (
                  <div className={`transition-all duration-300 ${
                    expandedSessions.has(session.id) ? 'block' : 'hidden'
                  }`}>
                    <div className="mt-4 p-4 bg-white/50 rounded-lg">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {session.description}
                      </p>
                      {session.category && session.category !== 'all' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm font-medium text-gray-600">{getCategoryLabel(session.category)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold text-gray-600">Where</span>
                            <span className="text-sm text-gray-600">{session.location}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {session.description && (
                <button
                  onClick={() => toggleSession(session.id)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-colors flex-shrink-0"
                >
                  {expandedSessions.has(session.id) ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Info */}
      <div className="mt-8 p-4 sm:p-6 bg-white rounded-lg border border-gray-200">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center">
          <Clock className="w-5 h-5 mr-2" style={{ color: '#C89116' }} />
          Važne informacije:
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <p className="mb-2">• B2B networking sesije se održavaju 6. oktobra od 13:30 do 15:30</p>
            <p className="mb-2">• Registracija za B2B sastanke je dostupna kroz networking aplikaciju</p>
          </div>
          <div>
            <p className="mb-2">• Ručak i networking od 15:30</p>
            <p className="mb-2">• Lokacija: Beogradski sajam, Hala 5</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            <strong>Glavna lokacija:</strong> Beogradski sajam, Hala 5, Beograd
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgrammePage;
